import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React from 'react';

import type { DisplayCreative } from '@feathr/blackbox';
import {
  getAspectRatioNotation,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import { CreativeThumbnail, RequirementsCard } from '@feathr/components';

import { isMetaDimensionComplete } from './CreativeRequirementsCard.utils';

interface IMetaDimensionItemProps {
  dimensionKey: string;
  creatives?: IObservableArray<DisplayCreative>;
}

/**
 * Renders a single Meta dimension item as either a standard item (image only)
 * or a MetaItem (image + video) depending on whether the dimension has a video counterpart.
 *
 * @param props - Component props with dimensionKey and optional creatives for completion
 * @returns JSX element or null if the key is not a valid Facebook dimension
 */
export function MetaDimensionItem({
  dimensionKey,
  creatives,
}: Readonly<IMetaDimensionItemProps>): JSX.Element | null {
  const imageDimension = ValidFacebookDimensions[dimensionKey];
  // Defensive: returns null for unknown dimension keys so
  // the component degrades gracefully if ValidFacebookDimensions changes.
  if (!imageDimension) {
    return null;
  }
  const { name, width, height } = imageDimension;
  const videoDimension = ValidFacebookVideoDimensions[dimensionKey];

  const imageComplete = creatives
    ? isMetaDimensionComplete(creatives, width, height, false)
    : undefined;

  const commonProps = {
    aspectRatio: getAspectRatioNotation(width / height),
    dimensions: dimensionKey,
    itemName: name,
  };

  if (!videoDimension) {
    return (
      <RequirementsCard.Item
        {...commonProps}
        thumbnail={
          <CreativeThumbnail
            height={height}
            isValidated={imageComplete ?? false}
            variant={'meta'}
            width={width}
          />
        }
      />
    );
  }

  const { width: videoWidth, height: videoHeight } = videoDimension;

  const videoComplete = creatives
    ? isMetaDimensionComplete(creatives, videoWidth, videoHeight, true)
    : undefined;

  return (
    <RequirementsCard.MetaItem
      {...commonProps}
      imageThumbnail={
        <CreativeThumbnail
          height={height}
          isValidated={imageComplete ?? false}
          variant={'meta'}
          width={width}
        />
      }
      videoThumbnail={
        <CreativeThumbnail
          height={videoHeight}
          isValidated={videoComplete ?? false}
          isVideo={true}
          variant={'meta'}
          width={videoWidth}
        />
      }
    />
  );
}
